/** The activity category record's header (plan §2 "Header", §4) — the activity record
 *  header's pattern on `PageHeader`, for a record that is little more than a name:
 *
 *  - Before the title, a 32 px tile with the category's icon as the app shows it, downsized
 *    (a link to the original, in a new tab), or a neutral initial when it has none — a
 *    hairline square, not the person record's navy avatar, so a missing icon reads as
 *    missing.
 *  - Title = the category's title (never empty: `Category #<id>`). No status chip: a category
 *    has no published state of its own, so there is nothing for `words.ts` to say here.
 *  - Subtitle: tenant · ID, each with its value actions.
 *  - Right: the Actions menu (its Danger group in Signal Red) and **Edit** (`E`) — or, in
 *    edit mode, **Close editor** (`Esc`). No manage permission → no Edit at all; editing
 *    switched off on the environment → Edit disabled, without its `E` hint, in a tab stop
 *    named "Edit — Editing is switched off" (the user record's blocked Edit), saying so in
 *    its tooltip and in the Actions menu's note. Another reason Edit cannot open yet
 *    (`editBlockedReason`) blocks it the same way, with its own words.
 *
 *  Pure over the detail payload; the page (after the record-core lift) owns the actions'
 *  handlers. */
import { Avatar, ButtonBase, Tooltip } from '@mui/material';

import type { ActivityCategoryDetail, ActivityCategoryRow } from '@/api/activityCategories';
import { PageHeader } from '@/components/PageHeader';
import { operatorActions } from '@/components/ValueActions/identity';
import { act, DetailSubtitle, ValueActions } from '@/components/ValueActions/ValueActions';
import { catalogEditGate } from '@/features/catalog/editGate';
import { RecordHeaderActions } from '@/features/records/header/RecordHeaderActions';
import type { RecordActionGroup } from '@/features/records/page/RecordActionsMenu';
import { activityCategoriesHref } from '@/utils/gridLinks';
import { IMAGE_PRESET, imageUrl, type ImageChunks } from '@/utils/imageUrl';

const ICON_PX = 32;

/** The icon as a tile shows it: DOWNSIZED through the resize service — except an SVG, which
 *  the service is never asked for (the apps load it as stored; a vector needs no resize).
 *  Null without an icon (or without the image host). */
export function categoryIconSrc(icon: ImageChunks | null | undefined): string | null {
  if (!icon) return null;
  return /\.svg$/i.test(icon.s) ? imageUrl(icon) : imageUrl(icon, IMAGE_PRESET.THUMB);
}

/** The category's title as the app shows it (never empty: `Category #<id>`). */
export function categoryTitle(c: Pick<ActivityCategoryRow, 'id' | 'title'>): string {
  return c.title?.trim() || `Category #${c.id}`;
}

/** The initial of a category without an icon ("#" when it has no title either). */
export function categoryInitial(c: Pick<ActivityCategoryRow, 'title'>): string {
  return (c.title?.trim().charAt(0) || '#').toUpperCase();
}

/** The icon tile: the icon downsized (a link to its original, in a new tab), or the neutral
 *  initial. The initial is also the image's fallback should it fail to load. 32 px in the
 *  header; the card's Icon field shows it larger (`size`). */
export function CategoryIcon({
  category,
  size = ICON_PX,
}: {
  category: Pick<ActivityCategoryRow, 'id' | 'title' | 'icon'>;
  size?: number;
}) {
  const src = categoryIconSrc(category.icon);
  const original = imageUrl(category.icon);
  const tile = {
    width: size,
    height: size,
    border: 1,
    borderColor: 'divider',
    // 15 px in the 32 px tile.
    fontSize: Math.round(size * 0.47),
    fontWeight: 600,
    color: 'text.secondary',
  } as const;
  if (!src) {
    return (
      <Avatar
        variant="rounded"
        aria-hidden
        sx={{ ...tile, bgcolor: 'background.default' }}
        data-category-icon="initial"
      >
        {categoryInitial(category)}
      </Avatar>
    );
  }
  return (
    <Tooltip title="Open the icon full size">
      <ButtonBase
        component="a"
        href={original ?? src}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`Open the icon of ${categoryTitle(category)} full size`}
        data-category-icon="image"
        sx={{
          borderRadius: 1,
          '&.Mui-focusVisible': {
            outline: '2px solid',
            outlineColor: 'primary.main',
            outlineOffset: 2,
          },
        }}
      >
        <Avatar
          variant="rounded"
          src={src}
          alt=""
          sx={{ ...tile, bgcolor: 'background.paper', '& img': { objectFit: 'contain' } }}
        >
          {categoryInitial(category)}
        </Avatar>
      </ButtonBase>
    </Tooltip>
  );
}

export interface CategoryRecordHeaderProps {
  detail: ActivityCategoryDetail;
  /** The active page tab's label (document title). */
  section: string;
  /** `console_activities_manage`: without it there is no Edit button at all. */
  canEdit: boolean;
  /** Editing is switched off on this environment (`CONSOLE_CATALOG_EDIT_ENABLED`). */
  editingDisabled?: boolean;
  /** With `canEdit`: why Edit cannot open yet — shown disabled with these words (its tab
   *  stop, tooltip and the Actions menu's note). */
  editBlockedReason?: string | null;
  editing?: boolean;
  onEdit?: () => void;
  onClose?: () => void;
  actionGroups?: RecordActionGroup[];
}

export function CategoryRecordHeader({
  detail: c,
  section,
  canEdit,
  editingDisabled = false,
  editBlockedReason = null,
  editing = false,
  onEdit,
  onClose,
  actionGroups = [],
}: CategoryRecordHeaderProps) {
  const title = categoryTitle(c);

  const subtitle = (
    <DetailSubtitle
      nowrap
      parts={[
        <ValueActions
          key="tenant"
          actions={operatorActions(c.tenant, {
            href: activityCategoriesHref({ tenant: c.tenant.id }),
            label: 'Categories of this operator',
          })}
        >
          {c.tenant.name || c.tenant.code}
        </ValueActions>,
        <ValueActions key="id" actions={[act.copy(String(c.id), 'Copy id')]}>
          ID {c.id}
        </ValueActions>,
      ]}
    />
  );

  const actions = (
    <RecordHeaderActions
      groups={actionGroups}
      editing={editing}
      onEdit={onEdit}
      onClose={onClose}
      {...catalogEditGate(canEdit, editingDisabled, editBlockedReason)}
    />
  );

  return (
    <PageHeader
      helpStory="activities"
      section={section}
      leading={<CategoryIcon category={c} />}
      title={title}
      titleText={title}
      subtitle={subtitle}
      actions={actions}
    />
  );
}
